import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/lib/auth';
import { useMyCompany, useCreateCompany, defaultsForCountry } from '@/lib/companies';
import { getGeoHint } from '@/lib/api';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { CountryPicker } from '@/components/ui/CountryPicker';
import { CurrencyPicker } from '@/components/ui/CurrencyPicker';
import { Combobox } from '@/components/ui/Combobox';
import { MultiCombobox } from '@/components/ui/MultiCombobox';
import { COUNTRIES } from '@/lib/countries';

export function OnboardingPage() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const user = useCurrentUser();
  const company = useMyCompany();
  const create = useCreateCompany();

  const [name, setName] = useState('');
  const [hqCountry, setHqCountry] = useState<string>('JP');
  const [otherCountries, setOtherCountries] = useState<string[]>([]);
  const [fiscalMonth, setFiscalMonth] = useState<number>(4);
  const [currency, setCurrency] = useState<string>('JPY');
  const [companyUrl, setCompanyUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Auto-detect HQ country once via geo hint
  useEffect(() => {
    void getGeoHint().then((g) => {
      if (g.country) {
        setHqCountry(g.country);
      }
    });
  }, []);

  // Apply per-country defaults whenever HQ changes
  useEffect(() => {
    const d = defaultsForCountry(hqCountry);
    setFiscalMonth(d.fiscalYearStartMonth);
    setCurrency(d.reportingCurrency);
  }, [hqCountry]);

  // Already onboarded → go straight to the app
  useEffect(() => {
    if (company.data) nav('/app', { replace: true });
  }, [company.data, nav]);

  // Not signed in → bounce to landing
  useEffect(() => {
    if (user.isSuccess && !user.data) nav('/', { replace: true });
  }, [user.isSuccess, user.data, nav]);

  const fiscalMonthLabel = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const date = new Date(2000, i, 1);
        return { value: i + 1, label: date.toLocaleString(i18n.language || 'en', { month: 'long' }) };
      }),
    [i18n.language],
  );

  // Build the multi-select items from the COUNTRIES master list,
  // excluding the HQ country (it's already accounted for above).
  const otherCountryItems = useMemo(
    () =>
      COUNTRIES.filter((c) => c.code !== hqCountry).map((c) => ({
        value: c.code,
        label: c.englishName,
        icon: c.flag,
        badge: c.code,
        searchAliases: [c.code],
      })),
    [hqCountry],
  );

  // Drop any selected country that becomes the HQ — otherwise it
  // could end up in both lists if the user changes HQ after picking.
  useEffect(() => {
    setOtherCountries((prev) => prev.filter((c) => c !== hqCountry));
  }, [hqCountry]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        name: name.trim(),
        hqCountryCode: hqCountry,
        countries: otherCountries,
        fiscalYearStartMonth: fiscalMonth,
        reportingCurrency: currency,
        primaryLocale: (i18n.language || 'en').split('-')[0],
        companyUrl: companyUrl.trim() || undefined,
      });
      nav('/app');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (err as { message?: string })?.message
        || 'Could not create company';
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  if (user.isLoading) return <PageLoading />;

  return (
    <div className="min-h-screen bg-ink-50">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-6 py-10 md:py-16">
        <p className="text-xs uppercase tracking-wide text-accent-600 font-medium mb-2">Step 1 of 1</p>
        <h1 className="display text-3xl md:text-4xl font-semibold tracking-tight text-ink-900 serif-jp">
          Tell us about your company
        </h1>
        <p className="mt-2 text-ink-600">
          90 seconds. We'll set up your workspace with the right fiscal-year, currency, and locale defaults.
        </p>

        <form onSubmit={submit} className="mt-8 rounded-2xl bg-white border border-ink-200 p-6 md:p-8 space-y-5">
          <Field label="Company name" required>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc."
              className="input"
            />
          </Field>

          <Field label="HQ country" required>
            <CountryPicker value={hqCountry} onChange={setHqCountry} />
            <p className="hint">Sets fiscal-year, currency, and locale defaults. JP → 4月 + 千円. US → Jan + USD.</p>
          </Field>

          <Field label="Other countries you employ in" optional>
            <MultiCombobox
              values={otherCountries}
              onChange={setOtherCountries}
              items={otherCountryItems}
              placeholder={t('onboarding.pickCountries', { defaultValue: 'Pick the countries where you employ people…' })}
              searchPlaceholder={t('onboarding.searchCountries', { defaultValue: 'Search countries…' })}
            />
            <p className="hint">Pick any number of countries. Used for cross-border payroll routing and compliance defaults.</p>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Fiscal-year start month" required>
              <Combobox<string>
                value={String(fiscalMonth)}
                onChange={(v) => setFiscalMonth(Number(v))}
                items={fiscalMonthLabel.map((m) => ({ value: String(m.value), label: m.label }))}
                hideSearch
                align="left"
              />
            </Field>

            <Field label="Reporting currency" required>
              <CurrencyPicker value={currency} onChange={setCurrency} />
            </Field>
          </div>

          <Field label="Company website" optional>
            <input
              type="url"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              placeholder="https://acme.com"
              className="input"
            />
            <p className="hint">If your sign-in email domain matches this URL, your workspace is auto-verified.</p>
          </Field>

          {error && (
            <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={!name.trim() || create.isPending}
              className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {create.isPending ? 'Creating workspace…' : 'Create workspace →'}
            </button>
          </div>
        </form>
      </main>
      <style>{`
        .hint { margin-top: 0.375rem; font-size: 0.7rem; color: #6B7280; }
      `}</style>
    </div>
  );
}

function Field({ label, required, optional, children }: { label: string; required?: boolean; optional?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-800 mb-1.5">
        {label}
        {required && <span className="text-warn-500 ms-1">*</span>}
        {optional && <span className="text-ink-400 text-xs ms-2 font-normal">optional</span>}
      </span>
      {children}
    </label>
  );
}

function PageLoading() {
  return (
    <div className="min-h-screen grid place-items-center text-ink-500 text-sm">Loading…</div>
  );
}
